import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
type CalculatorType = 'retirement' | 'savings' | 'mortgage';
interface CalculatorProps {
  type: CalculatorType;
  title: string;
  description: string;
}
export function CalculatorCard({
  type,
  title,
  description
}: CalculatorProps) {
  const [values, setValues] = useState<Record<string, number>>({});
  const [result, setResult] = useState<number | null>(null);
  // Initialize defaults based on type
  useEffect(() => {
    if (type === 'retirement') {
      setValues({
        age: 30,
        savings: 50000,
        contribution: 1000
      });
    } else if (type === 'savings') {
      setValues({
        target: 100000,
        years: 5,
        interest: 5
      });
    } else if (type === 'mortgage') {
      setValues({
        loan: 400000,
        interest: 4.5,
        years: 30
      });
    }
  }, [type]);
  const handleChange = (key: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };
  useEffect(() => {
    // Simple calculation logic for demo purposes
    if (type === 'retirement') {
      const {
        age,
        savings,
        contribution
      } = values;
      const yearsToRetire = 65 - (age || 30);
      if (yearsToRetire > 0) {
        // Simplified compound interest
        const r = 0.07 / 12;
        const n = yearsToRetire * 12;
        const futureValue = (savings || 0) * Math.pow(1 + r, n) + (contribution || 0) * ((Math.pow(1 + r, n) - 1) / r);
        setResult(futureValue);
      }
    } else if (type === 'savings') {
      const {
        target,
        years,
        interest
      } = values;
      const r = (interest || 0) / 100 / 12;
      const n = (years || 0) * 12;
      if (r > 0 && n > 0) {
        const monthly = (target || 0) * r / (Math.pow(1 + r, n) - 1);
        setResult(monthly);
      }
    } else if (type === 'mortgage') {
      const {
        loan,
        interest,
        years
      } = values;
      const r = (interest || 0) / 100 / 12;
      const n = (years || 0) * 12;
      if (r > 0 && n > 0) {
        const monthly = (loan || 0) * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        setResult(monthly);
      }
    }
  }, [values, type]);
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  return <div className="swiss-card flex flex-col h-full relative group" data-id="element-68">
      <div className="mb-8" data-id="element-69">
        <h3 className="text-2xl font-bold mb-2" data-id="element-70">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed" data-id="element-71">{description}</p>
      </div>

      <div className="space-y-6 flex-grow" data-id="element-72">
        {type === 'retirement' && <>
            <div className="space-y-2" data-id="element-73">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-74">
                Current Age
              </label>
              <input type="number" className="swiss-input" value={values.age || ''} onChange={e => handleChange('age', e.target.value)} data-id="element-75" />
            </div>
            <div className="space-y-2" data-id="element-76">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-77">
                Current Savings
              </label>
              <input type="number" className="swiss-input" value={values.savings || ''} onChange={e => handleChange('savings', e.target.value)} data-id="element-78" />
            </div>
            <div className="space-y-2" data-id="element-79">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-80">
                Monthly Contribution
              </label>
              <input type="number" className="swiss-input" value={values.contribution || ''} onChange={e => handleChange('contribution', e.target.value)} data-id="element-81" />
            </div>
          </>}

        {type === 'savings' && <>
            <div className="space-y-2" data-id="element-82">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-83">
                Target Amount
              </label>
              <input type="number" className="swiss-input" value={values.target || ''} onChange={e => handleChange('target', e.target.value)} data-id="element-84" />
            </div>
            <div className="space-y-2" data-id="element-85">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-86">
                Timeframe (Years)
              </label>
              <input type="number" className="swiss-input" value={values.years || ''} onChange={e => handleChange('years', e.target.value)} data-id="element-87" />
            </div>
            <div className="space-y-2" data-id="element-88">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-89">
                Interest Rate (%)
              </label>
              <input type="number" className="swiss-input" value={values.interest || ''} onChange={e => handleChange('interest', e.target.value)} data-id="element-90" />
            </div>
          </>}

        {type === 'mortgage' && <>
            <div className="space-y-2" data-id="element-91">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-92">
                Loan Amount
              </label>
              <input type="number" className="swiss-input" value={values.loan || ''} onChange={e => handleChange('loan', e.target.value)} data-id="element-93" />
            </div>
            <div className="space-y-2" data-id="element-94">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-95">
                Interest Rate (%)
              </label>
              <input type="number" className="swiss-input" value={values.interest || ''} onChange={e => handleChange('interest', e.target.value)} data-id="element-96" />
            </div>
            <div className="space-y-2" data-id="element-97">
              <label className="text-xs uppercase font-bold tracking-wider" data-id="element-98">
                Loan Term (Years)
              </label>
              <input type="number" className="swiss-input" value={values.years || ''} onChange={e => handleChange('years', e.target.value)} data-id="element-99" />
            </div>
          </>}
      </div>

      <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-100">
        <div className="text-xs uppercase font-bold tracking-wider text-gray-500 mb-1" data-id="element-101">
          {type === 'retirement' ? 'Projected Fund' : 'Monthly Payment'}
        </div>
        <div className="text-3xl font-bold text-[var(--color-swiss-red)]" data-id="element-102">
          {result ? formatCurrency(result) : '...'}
        </div>
      </div>

      <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-103">
        <ArrowRight className="w-5 h-5 text-[var(--color-swiss-red)]" data-id="element-104" />
      </div>
    </div>;
}